import { jsx as _jsx } from "react/jsx-runtime";
import { getAdvanceBookingPeriodForShowtime, getFetchedShowtimeById, LoadingState, loadShowtimeById } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { isPast } from 'date-fns';
import { observer } from 'mobx-react';
import { useCallback } from 'react';
import { useLoadActionForData } from '../../hooks/use-load-action-for-data';
import { useMemberRewards } from '../../hooks/use-member-rewards';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { ShowtimeContextMessage, ShowtimeContextMessageType } from './subcomponents/message';
import { ShowtimeContextInstance } from './types/context';
import { defaultShowtimeContextProps, showtimeContextPropsValidator } from './types/props';
const b = bemClassBuilder('v-showtime-context');
const ShowtimeContext = observer(props => {
    const { showtimeId } = props;
    const loadAction = useCallback(() => loadShowtimeById(showtimeId), [showtimeId]);
    const fetchedShowtime = getFetchedShowtimeById(showtimeId);
    const { loadingState, data: showtime } = useLoadActionForData(loadAction, fetchedShowtime);
    // Load member rewards to show reward-restricted prices and booking periods.
    useMemberRewards();
    const messageProps = getMessageProps(props, showtimeId, loadingState);
    if (messageProps)
        return _jsx(ShowtimeContextMessage, Object.assign({}, messageProps));
    return (_jsx(ShowtimeContextInstance.Provider, Object.assign({ value: { showtime, showtimeId } }, { children: _jsx("div", Object.assign({ className: b.block({ loading: !showtime }) }, { children: props.children })) })));
});
ShowtimeContext.displayName = 'ShowtimeContext';
/**
 * The **Showtime Context** component loads the showtime details from the API and provides it to its children via the `ShowtimeContextInstance`.
 * It will render a single error message with an action button if the showtime fails to load.
 */
export const ShowtimeContextComponent = withComponentWrapper(ShowtimeContext, defaultShowtimeContextProps, showtimeContextPropsValidator);
const getMessageProps = (props, showtimeId, loadingState) => {
    switch (loadingState) {
        case LoadingState.Failure:
            return Object.assign({ messageType: ShowtimeContextMessageType.FailedToLoadData }, props);
        case LoadingState.Success: {
            const bookingPeriod = getAdvanceBookingPeriodForShowtime(showtimeId);
            if (!bookingPeriod || isPast(new Date(bookingPeriod.startsAt)))
                return;
            return Object.assign({ messageType: ShowtimeContextMessageType.FutureAdvanceBookingPeriod, bookingPeriod }, props);
        }
        default:
            return undefined;
    }
};
